import { Component, OnInit } from '@angular/core';
import { TranslateService }  from '@ngx-translate/core';

import { CompanyService }      from '../companies/company.service';
import { ImportHubCardModel }  from './typings/imports.hub';
import { ImportHubStateModel } from './typings/imports.hub';
import { ImportOptionsModel }  from './typings/imports.option';
import { ImportsService }      from './imports.service';
import { SessionService }      from '../sessions/session.service';

// WS-6753. One page presenting bringing a customer's data across as a single job.
//
// The page it replaces is eleven collapsible panels, each with its own file picker and
// its own copy of the same mapping grid. It never said what order to do them in, so the
// person onboarding was expected to already know that customers come before vehicles
// and products before bill-of-materials items. Guess wrong and the rows fail for
// reasons that look nothing to do with ordering. It also remembered nothing, which
// matters because the work almost always spans more than one day.
@Component({
  selector   : 'wor-import-hub',
  styles: [ require('./import.hub.component.scss') ],
  template: require('./import.hub.component.html')
})
export class ImportHubComponent implements OnInit {
  // The order below is the order to do them in. The requires entries are the only four
  // dependencies the backend actually enforces, taken from the engine's own skip_create
  // guards rather than assumed:
  //
  //   Vehicle needs Customer, unless it is a Unit
  //   VehicleHistoryItem needs VehicleHistory
  //   BomItem needs BomProduct
  //   ProductSerialNumber needs Product
  //
  // VehicleHistory referencing a Vehicle is real, and ignoring it produces history
  // attached to nothing, but the backend does not refuse the row. So it advises rather
  // than locks. Locking on a rule the backend does not apply would be inventing one.
  cards : Array<ImportHubCardModel> = [
    {
      tableName     : 'Customer',
      forTable     : 'customers',
      labelKey      : 'IMPORT_SPACE.HUB_CUSTOMERS',
      url           : '/imports/perform_customer_import',
      hidesFromBosch: true
    },
    {
      tableName     : 'Vendor',
      forTable     : 'vendors',
      labelKey      : 'IMPORT_SPACE.HUB_VENDORS',
      url           : '/imports/perform_vendor_import',
      hidesFromBosch: true
    },
    {
      // The only one the old page showed to everybody, with no guard at all.
      tableName: 'Product',
      forTable: 'products',
      labelKey : 'IMPORT_SPACE.HUB_PRODUCTS',
      url      : '/imports/perform_product_import'
    },
    {
      tableName     : 'Vehicle',
      forTable     : 'vehicles',
      labelKey      : 'IMPORT_SPACE.HUB_VEHICLES',
      url           : '/imports/perform_vehicle_import',
      requires      : 'Customer',
      hidesFromBosch: true
    },
    {
      // Guarded on the dealership and the subscription, and never on the brand.
      tableName      : 'Unit',
      forTable      : 'units',
      labelKey       : 'IMPORT_SPACE.HUB_UNITS',
      url            : '/imports/perform_unit_import',
      needsDealership: true,
      needsSilver    : true
    },
    {
      tableName     : 'ProductSerialNumber',
      forTable     : 'product_serial_numbers',
      labelKey      : 'IMPORT_SPACE.HUB_SERIAL_NUMBERS',
      url           : '/imports/perform_serial_number_import',
      requires      : 'Product',
      hidesFromBosch: true,
      needsSilver   : true
    },
    {
      tableName     : 'BomProduct',
      forTable     : 'bom_header',
      labelKey      : 'IMPORT_SPACE.HUB_BOM_HEADERS',
      url           : '/imports/perform_bomHeader_import',
      requires      : 'Product',
      hidesFromBosch: true,
      needsSilver   : true
    },
    {
      tableName     : 'BomItem',
      forTable     : 'bom_items',
      labelKey      : 'IMPORT_SPACE.HUB_BOM_ITEMS',
      url           : '/imports/perform_bomItems_import',
      requires      : 'BomProduct',
      hidesFromBosch: true,
      needsSilver   : true
    },
    {
      tableName     : 'VehicleHistory',
      forTable     : 'vehicle_histories',
      labelKey      : 'IMPORT_SPACE.HUB_HISTORY_HEADERS',
      url           : '/imports/perform_historyh_import',
      advises       : 'Vehicle',
      hidesFromBosch: true,
      needsSilver   : true
    },
    {
      tableName     : 'VehicleHistoryItem',
      forTable     : 'vehicle_history_items',
      labelKey      : 'IMPORT_SPACE.HUB_HISTORY_LINES',
      url           : '/imports/perform_historyl_import',
      requires      : 'VehicleHistory',
      hidesFromBosch: true,
      needsSilver   : true
    }
  ];

  completed : { [ tableName : string ] : ImportHubStateModel } = {};
  loading   = false;
  selected  : ImportHubCardModel;

  // Built when a card is opened, once the field list and the correction list are back.
  // Held here rather than assembled in the template, because the template cannot wait
  // for a promise and an import opened with empty lists cannot be completed at all.
  importOptions : ImportOptionsModel;

  // True while the two lists are being fetched, so the mapping screen is not shown
  // half-built.
  opening = false;

  // Where a proposed mapping is asked for. One endpoint for every import type; the type
  // itself is sent as forTable.
  static readonly PROPOSAL_URL = '/imports/propose_import_mapping';

  constructor (
    protected companyService  : CompanyService,
    protected importsService  : ImportsService,
    protected sessionService  : SessionService,
    protected translateService: TranslateService
  ) {}

  ngOnInit () : void {
    this.refresh();
  }

  // WS-6774. Exactly the guards the page this replaces applied, carried over rather
  // than reasoned about. They are not uniform and getting one wrong shows an import to
  // a company that cannot see it today.
  isVisible ( card : ImportHubCardModel ) : boolean {
    if ( card.hidesFromBosch && this.companyService.isBrandPuppet('bosch') ) {
      return false;
    }

    if ( card.needsSilver && !this.sessionService.base()?.silver_or_higher ) {
      return false;
    }

    if ( card.needsDealership && !this.companyService.hasDealership() ) {
      return false;
    }

    return true;
  }

  get visibleCards () : Array<ImportHubCardModel> {
    return this.cards.filter(card => this.isVisible(card));
  }

  // Progress is counted over what this company can actually see. Counting a hidden type
  // would leave a finished job permanently short of complete, through no fault of the
  // person doing it.
  get jobCards () : Array<ImportHubCardModel> {
    return this.visibleCards;
  }

  get completedCount () : number {
    return this.jobCards.filter(card => this.isComplete(card)).length;
  }

  get totalCount () : number {
    return this.jobCards.length;
  }

  get progressPercentage () : number {
    return this.totalCount
      ? Math.round(( this.completedCount / this.totalCount ) * 100)
      : 0;
  }

  isComplete ( card : ImportHubCardModel ) : boolean {
    return !!this.completed[ card.tableName ];
  }

  // Locked until its prerequisite has actually finished for this company.
  isLocked ( card : ImportHubCardModel ) : boolean {
    return !!card.requires && !this.completed[ card.requires ];
  }

  // Advisory only: the type can be started, but doing it now produces records pointing
  // at nothing.
  isAdvised ( card : ImportHubCardModel ) : boolean {
    return !!card.advises && !this.completed[ card.advises ] && !this.isLocked(card);
  }

  blockerLabelKey ( card : ImportHubCardModel ) : string {
    const blocker = this.cards.find(other => other.tableName === ( card.requires || card.advises ));

    return blocker ? blocker.labelKey : '';
  }

  recordCount ( card : ImportHubCardModel ) : number {
    const state = this.completed[ card.tableName ];

    return state ? ( state.number_created || 0 ) + ( state.number_matched || 0 ) : 0;
  }

  // A locked card cannot be opened. Enforced here rather than only hidden in the
  // template, so the rule holds however the card is reached.
  //
  // Opening fetches the two lists the mapping screen needs before showing it. They are
  // per import type, so they cannot be fetched once for the hub, and the screen is
  // unusable without them: no field to map onto and no correction to apply.
  select ( card : ImportHubCardModel ) : Promise<void> {
    if ( this.isLocked(card) || !this.isVisible(card) ) {
      return Promise.resolve();
    }

    this.opening = true;

    return Promise.all([
      this.importsService.fetchOurColumns(card.forTable),
      this.importsService.fetchTransformFunctions(card.forTable)
    ])
    .then(([ ourColumns, transformFunctions ]) => {
      this.importOptions = {
        collapsed         : false,
        forTable          : card.forTable,
        name              : card.tableName,
        ourColumns        : ourColumns,
        placeholder       : '',
        proposalUrl       : ImportHubComponent.PROPOSAL_URL,
        title             : this.translateService.instant(card.labelKey),
        transformFunctions: transformFunctions,
        url               : card.url
      };

      this.selected = card;
    })
    .finally(() => {
      this.opening = false;
    });
  }

  close () : void {
    this.selected      = null;
    this.importOptions = null;

    this.refresh();
  }

  refresh () : Promise<void> {
    this.loading = true;

    return this.importsService.fetchHubState()
    .then(state => {
      this.completed = ( state || [] ).reduce(( map, entry ) => {
        map[ entry.table_name ] = entry;

        return map;
      }, {} as { [ tableName : string ] : ImportHubStateModel });
    })
    .finally(() => {
      this.loading = false;
    });
  }
}
